import { Controller } from "@hotwired/stimulus"
import { loadGoogleMaps } from "../support/google_maps_loader"

export default class extends Controller {
  static targets = [
    "address",
    "city",
    "streetNumber",
    "route",
    "postalCode",
    "country",
    "countryShort",
    "county",
    "state",
    "longitude",
    "latitude",
    "placeId",
    "formatted",
    "addressOne",
    "addressTwo",
    "name",
    "url",
    "types",
    "iconUrl",
    "location",
    "viewportNortheast",
    "viewportSouthwest",
    "summary",
    "deleteButton"
  ]

  static values = {
    country: Array
  }

  initialize() {
    this.placeChanged = this.placeChanged.bind(this)
    this.restoreAddressOnBlur = this.restoreAddressOnBlur.bind(this)
    this.selectAllOnFocus = this.selectAllOnFocus.bind(this)
    this.handleKeydown = this.handleKeydown.bind(this)
    this.initOnFocus = this.initOnFocus.bind(this)
    this.originalAddressText = ""
    this.initialized = false
  }

  connect() {
    // Store the original address text for restoration on blur
    if (this.hasFormattedTarget && this.hasNameTarget && this.hasAddressTarget) {
      const name = this.nameTarget.value || ""
      const formatted = this.formattedTarget.value || ""
      this.originalAddressText = name ? `${name}${formatted ? ', ' : ''}${formatted}` : formatted

      // Add event listeners
      this.addressTarget.addEventListener('blur', this.restoreAddressOnBlur)
      this.addressTarget.addEventListener('focus', this.initOnFocus)
      this.addressTarget.addEventListener('keydown', this.handleKeydown)
    }
  }

  disconnect() {
    this.pacContainerObserver?.disconnect()
    this.pacContainerObserver = null

    if (this.hasAddressTarget) {
      this.addressTarget.removeEventListener('blur', this.restoreAddressOnBlur)
      this.addressTarget.removeEventListener('focus', this.initOnFocus)
      this.addressTarget.removeEventListener('keydown', this.handleKeydown)
    }
  }

  async initOnFocus(event) {
    this.selectAllOnFocus(event)

    if (this.initialized || this.loading) return

    this.loading = true
    try {
      await loadGoogleMaps()
      if (!this.element.isConnected || this.initialized) return

      this.initAutocomplete()
      this.initialized = true
    } catch (error) {
      console.error("Failed to initialize place autocomplete:", error)
    } finally {
      this.loading = false
    }
  }

  initAutocomplete() {
    const dialog = this.addressTarget.closest('dialog[open]')
    if (dialog) this.watchForAutocompleteContainer(dialog)

    try {
      this.autocomplete = new google.maps.places.Autocomplete(
        this.addressTarget,
        this.autocompleteOptions
      )
    } catch (error) {
      this.pacContainerObserver?.disconnect()
      this.pacContainerObserver = null
      throw error
    }

    this.moveAutocompleteContainerInto(dialog, { allowUnambiguousContainer: true })
    this.autocomplete.addListener("place_changed", this.placeChanged)
  }

  watchForAutocompleteContainer(dialog) {
    this.pacContainerObserver?.disconnect()
    this.existingPacContainers = new Set(document.querySelectorAll("body > .pac-container"))
    this.pacContainerObserver = new MutationObserver(() => this.moveAutocompleteContainerInto(dialog))
    this.pacContainerObserver.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["style", "id"]
    })
  }

  moveAutocompleteContainerInto(dialog, { allowUnambiguousContainer = false } = {}) {
    if (!dialog) return

    const newContainers = [...document.querySelectorAll("body > .pac-container")]
      .filter((candidate) => !this.existingPacContainers.has(candidate))
    const ownedContainerIds = ["aria-controls", "aria-owns"]
      .flatMap((attribute) => (this.addressTarget.getAttribute(attribute) || "").split(/\s+/))
      .filter(Boolean)
    const inputRect = this.addressTarget.getBoundingClientRect()
    const geometricallyOwnedContainer = newContainers.find((candidate) => {
      const candidateRect = candidate.getBoundingClientRect()
      return candidateRect.width > 0 &&
        Math.abs(candidateRect.left - inputRect.left) <= 1 &&
        Math.abs(candidateRect.top - inputRect.bottom) <= 1 &&
        Math.abs(candidateRect.width - inputRect.width) <= 4
    })
    const container = newContainers.find((candidate) => ownedContainerIds.includes(candidate.id)) ||
      geometricallyOwnedContainer ||
      (allowUnambiguousContainer && newContainers.length === 1 ? newContainers[0] : null)
    if (!container) return

    this.pacContainerObserver?.disconnect()
    this.pacContainerObserver = null
    container.addEventListener("mousedown", (event) => event.stopPropagation())
    container.addEventListener("click", (event) => event.stopPropagation())
    dialog.appendChild(container)
  }

  selectAllOnFocus(event) {
    // Select all text in the input field when it receives focus
    event.target.select()
  }

  handleKeydown(event) {
    // Handle Enter key to confirm removal of address
    if (event.key === 'Enter' && !this.addressTarget.value.trim()) {
      event.preventDefault()
      this.removePlace({ preventDefault: () => {} })
    } else if (event.key === 'Enter') {
      event.preventDefault()
    }
  }

  placeChanged() {
    this.place = this.autocomplete.getPlace()

    // Check if the place has details (if not, it might be an empty search or not a valid place)
    if (!this.place.place_id) {
      return
    }

    const addressComponents = this.place.address_components
    let formattedAddress = undefined
    if (addressComponents !== undefined) {
      formattedAddress = this.formatAddressComponents(addressComponents)

      this.setAddressComponents(formattedAddress)
    }

    if (this.place.geometry !== undefined) {
      this.setGeometry(this.place.geometry)
    }

    if (this.hasPlaceIdTarget)
      this.placeIdTarget.value = this.place.place_id || ""

    if (this.hasFormattedTarget)
      this.formattedTarget.value = this.place.formatted_address || ""

    if (this.hasUrlTarget)
      this.urlTarget.value = this.place.url || ""

    if (this.hasNameTarget)
      this.nameTarget.value =
          (this.addressOneTarget?.value != this.place.name) ? this.place.name || "" : ""

    if (this.hasIconUrlTarget)
      this.iconUrlTarget.value = this.place.icon || ""

    if (this.hasTypesTarget)
      this.typesTarget.value = JSON.stringify(this.place.types) || ""


    if (this.hasLocationTarget)
      this.locationTarget.value = this.place.geometry.location
        ? this.place.geometry.location.toString()
        : ""

    if (this.hasSummaryTarget)
      this.summaryTarget.innerHTML = this.nameTarget.value  + (this.nameTarget.value ?  ", " : "") + this.formattedTarget.value || ""

    // Update the original address text for future restoration
    const name = this.nameTarget.value || ""
    const formatted = this.formattedTarget.value || ""
    this.originalAddressText = name ? `${name}${formatted ? ', ' : ''}${formatted}` : formatted

    // Show the delete button
    if (this.hasDeleteButtonTarget) {
      this.deleteButtonTarget.style.display = ''
    }

    /*if (this.hasViewportNortheastTarget)
      this.viewportNortheastTarget.value = this.place.geometry.viewport.northeast
        ? this.place.geometry.viewport.northeast.toString()
        : ""

    if (this.hasViewportSouthwestTarget)
      this.viewportSouthwestTarget.value = this.place.geometry.viewport.southwest
        ? this.place.geometry.viewport.southwest.toString()
        : ""*/
  }

  restoreAddressOnBlur() {
    // Always restore the original address on blur unless a valid place was selected
    if (this.hasAddressTarget && this.hasFormattedTarget) {
      const currentInputValue = this.addressTarget.value
      const storedFormattedValue = this.formattedTarget.value

      // If there's a stored address, restore it
      if (storedFormattedValue && currentInputValue !== this.originalAddressText) {
        this.addressTarget.value = this.originalAddressText
      } else if (!storedFormattedValue && currentInputValue.trim() === '') {
        // If there's no stored address and the field is empty, keep it empty
        this.addressTarget.value = ''
      }
    }
  }

  removePlace(event) {
    event.preventDefault()

    // Mark the place for destruction if it has an ID
    const idField = this.element.querySelector('input[name$="[id]"]')
    const destroyField = this.element.querySelector('input[name$="[_destroy]"]')

    if (idField && idField.value && destroyField) {
      // If we have an ID and a _destroy field, mark for destruction
      destroyField.value = "1"

      // Make sure the _destroy field is not disabled
      destroyField.disabled = false

      // Hide the delete button since we've marked for destruction
      if (this.hasDeleteButtonTarget) {
        this.deleteButtonTarget.style.display = 'none'
      }
    } else {
      // Otherwise clear all place field values
      const fieldsToReset = [
        "city", "postalCode", "country", "countryShort", "placeId",
        "addressOne", "addressTwo", "name", "formatted", "url",
        "iconUrl", "types", "location"
      ]

      fieldsToReset.forEach(field => {
        const target = `${field}Target`
        if (this[`has${field.charAt(0).toUpperCase() + field.slice(1)}Target`]) {
          this[target].value = ""
        }
      })

      // Clear the address field
      if (this.hasAddressTarget) {
        this.addressTarget.value = ""
        this.addressTarget.focus() // Focus back on the search field
      }

      // Hide the delete button
      if (this.hasDeleteButtonTarget) {
        this.deleteButtonTarget.style.display = 'none'
      }
    }

    // Clear the summary display
    if (this.hasSummaryTarget) {
      this.summaryTarget.innerHTML = ""
    }

    // Reset the original address text
    this.originalAddressText = ""

    // Dispatch a generic event to notify the expandable-fields controller to collapse
    const expandableFieldsElement = this.element.closest('[data-controller="expandable-fields"]')
    if (expandableFieldsElement) {
      expandableFieldsElement.dispatchEvent(new CustomEvent('expandable-field:cleared'))
    }
  }

  setAddressComponents(address) {
    if (this.hasStreetNumberTarget)
      this.streetNumberTarget.value = address.street_number || ""

    if (this.hasRouteTarget)
      this.routeTarget.value = address.route || ""

    if (this.hasCityTarget)
      this.cityTarget.value = address.locality || ""

    if (this.hasCountyTarget)
      this.countyTarget.value = address.administrative_area_level_2 || ""

    if (this.hasStateTarget)
      this.stateTarget.value = address.administrative_area_level_1 || ""

    if (this.hasCountryTarget)
      this.countryTarget.value = address.country || ""

    if (this.hasCountryShortTarget)
      this.countryShortTarget.value = address.country_short || ""

    if (this.hasPostalCodeTarget)
      this.postalCodeTarget.value = address.postal_code || ""

    if (this.hasAddressOneTarget) {
      const streetNumber = address.street_number || '';
      const route = address.route || '';
      this.addressOneTarget.value = [route, streetNumber].filter(Boolean).join(' ').trim();
    }

    if (this.hasAddressTwoTarget) {
      this.addressTwoTarget.value = ''; // Leave empty for now
    }
  }

  setGeometry(geometry) {
    if (this.hasLongitudeTarget)
      this.longitudeTarget.value = geometry.location.lng().toString()
    if (this.hasLatitudeTarget)
      this.latitudeTarget.value = geometry.location.lat().toString()
  }

  get autocompleteOptions() {
    const options = {
      fields: [
        'address_components',
        'type',
        'geometry',
        'place_id',
        'formatted_address',
        'photos',
        'website',
        'url',
        'name',
        'icon',
        'types'
      ],
      bounds: new google.maps.LatLngBounds(
        new google.maps.LatLng(51.619139, 7.423895999999999), // Southwest corner
        new google.maps.LatLng(51.619139, 7.423895999999999)  // Northeast corner
      ),
    }
  }

  preventSubmit(event) {
    if (event.code === "Enter") {
      event.preventDefault()
    }
  }

  formatAddressComponents(addressComponents) {
    const data = {}

    addressComponents.forEach(component => {
      const type = component.types[0]

      data[type] = component.long_name
      data[type + "_short"] = component.short_name
    })

    return data
  }
}
